"use client";

import { Check, User } from "lucide-react";
import { FeedEvents, UserEvents, trackEvent } from "@/analytics";
import { cn } from "@punchup/utils/cn";
import { genAbsoluteURL } from "@punchup/utils/genAbsoluteURL";

import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { Follow } from "@/components/checkout";
import Image from "next/image";
import Link from "next/link";
import { SpecialRow } from "@/data/specials";
import { WatchCTAButton } from "@/components/special";
import { TaggedComedian } from "@/components/feed/types";

interface VideoFeedHeaderMobileProps {
  comedian: Comedian;
  special: SpecialRow;
  postAge: number;
  profileImg: string | null;
  userFollowsComedian: boolean;
  isLoggedOutState: boolean;
  redirectTo: string;
  followCallback: () => Promise<void>;
  renderTimestamp: (postAge: number) => string;
  taggedComedians?: TaggedComedian[];
}

export default function VideoFeedHeaderMobile({
  comedian,
  special,
  postAge,
  profileImg,
  userFollowsComedian,
  isLoggedOutState,
  redirectTo,
  followCallback,
  renderTimestamp,
  taggedComedians,
}: VideoFeedHeaderMobileProps) {
  const comedianPageUrl = `/${comedian.slug}`;
  const showNew = true;

  return showNew ? (
    <VideoFeedHeaderMobileNew
      comedian={comedian}
      special={special}
      postAge={postAge}
      profileImg={profileImg}
      userFollowsComedian={userFollowsComedian}
      isLoggedOutState={isLoggedOutState}
      redirectTo={redirectTo}
      followCallback={followCallback}
      renderTimestamp={renderTimestamp}
      comedianPageUrl={comedianPageUrl}
      taggedComedians={taggedComedians}
    />
  ) : (
    <VideoFeedHeaderMobileOld
      comedian={comedian}
      special={special}
      postAge={postAge}
      profileImg={profileImg}
      userFollowsComedian={userFollowsComedian}
      isLoggedOutState={isLoggedOutState}
      redirectTo={redirectTo}
      followCallback={followCallback}
      renderTimestamp={renderTimestamp}
    />
  );
}

function VideoFeedHeaderMobileNew({
  comedian,
  special,
  postAge,
  profileImg,
  userFollowsComedian,
  isLoggedOutState,
  redirectTo,
  followCallback,
  renderTimestamp,
  comedianPageUrl,
  taggedComedians,
}: {
  comedian: Comedian;
  special: SpecialRow;
  postAge: number;
  profileImg: string | null;
  userFollowsComedian: boolean;
  isLoggedOutState: boolean;
  redirectTo: string;
  followCallback: () => Promise<void>;
  renderTimestamp: (postAge: number) => string;
  comedianPageUrl: string;
  taggedComedians?: TaggedComedian[];
}) {
  // Style variables - with highlighted backgrounds for debugging
  const profileContainerStyles = cn([
    "flex-shrink-0",
    // "bg-blue-400", // PROFILE AVATAR CONTAINER
    "w-10",
    "h-10",
    "rounded-full",
    "overflow-hidden",
  ]);

  const profileLinkStyles = cn(["block", "w-full", "h-full"]);

  const profileImageStyles = cn([
    "w-full",
    "h-full",
    "object-cover",
    "rounded-full",
  ]);

  const profilePlaceholderStyles = cn([
    "w-10",
    "h-10",
    "rounded-full",
    // "bg-purple-400", // PROFILE PLACEHOLDER (when no image)
    "bg-gray-200",
    "flex",
    "items-center",
    "justify-center",
  ]);

  const profileIconStyles = cn(["w-6", "h-6", "text-gray-400"]);

  const contentContainerStyles = cn([
    "flex",
    "flex-col",
    "flex-1",
    "min-w-0",
    // "bg-green-400" // MAIN CONTENT CONTAINER
  ]);

  const headerRowStyles = cn([
    "flex",
    "items-center",
    "justify-between",
    "w-full",
    "h-10", // Match avatar height
    // "bg-yellow-400" // HEADER ROW (name + action)
  ]);

  const nameColumnStyles = cn([
    "flex",
    "flex-col",
    "justify-center", // Center content vertically
    "gap-0.5", // Tight spacing between lines
    // "bg-pink-400" // NAME COLUMN (comedian name + timestamp)
  ]);

  const timestampStyles = cn(["text-white/80", "text-sm"]);

  const specialTitleStyles = cn([
    "text-white",
    "font-bold",
    "text-sm", // Decreased from text-base
    "line-clamp-1",
    "leading-tight", // Tighter line height
  ]);

  const comedianNameStyles = cn([
    "text-white/80",
    "text-sm",
    "hover:underline",
  ]);

  const actionContainerStyles = cn([
    "ml-2",
    "flex-shrink-0",
    "flex",
    "items-center", // Center vertically with avatar
    // "bg-orange-400" // ACTION CONTAINER (subscribe/price button)
  ]);

  const purchaseLinkStyles = cn([
    "bg-primary",
    "text-white",
    "px-3",
    "py-1",
    "rounded-md",
    "text-xs",
    "font-medium",
  ]);

  const subscribedIndicatorStyles = cn([
    "flex",
    "items-center",
    "gap-1",
    "text-white",
    "text-xs",
    "-mt-0.5",
    // "bg-cyan-400" // SUBSCRIBED INDICATOR
  ]);
  return (
    <>
      <div className={profileContainerStyles}>
        <Link
          href={comedianPageUrl}
          onClick={() =>
            trackEvent(FeedEvents.CLICKED_COMEDIAN_AVATAR_VIDEO_CARD)
          }
          className={profileLinkStyles}
        >
          {profileImg ? (
            <Image
              src={profileImg}
              width={40}
              height={40}
              alt={`${comedian.display_name}'s profile picture`}
              className={profileImageStyles}
            />
          ) : (
            <div className={profilePlaceholderStyles}>
              <User className={profileIconStyles} />
            </div>
          )}
        </Link>
      </div>
      <div className={contentContainerStyles}>
        <div className={headerRowStyles}>
          <div className={nameColumnStyles}>
            <Link
              href={comedianPageUrl}
              onClick={() =>
                trackEvent(FeedEvents.CLICKED_COMEDIAN_NAME_VIDEO_CARD)
              }
              className={comedianNameStyles}
            >
              <span>{comedian.display_name}</span>
            </Link>
            {taggedComedians && taggedComedians.length > 0 && (
              <p className={timestampStyles}>
                ft. {taggedComedians.map((c) => c.display_name).join(", ")}
              </p>
            )}
            <p className={timestampStyles}>{renderTimestamp(postAge)}</p>
          </div>
          {special.requires_purchase ? (
            <div className={actionContainerStyles}>
              <Link
                href={`/${comedian.slug}/${special.slug}/purchase`}
                className={purchaseLinkStyles}
              >
                <WatchCTAButton special={special} />
              </Link>
            </div>
          ) : !(taggedComedians && taggedComedians.length > 0) ? (
            <div className={actionContainerStyles}>
              {userFollowsComedian ? (
                <div className={subscribedIndicatorStyles}>
                  <Check width={12} height={12} strokeWidth={3} />
                  <span>Subscribed</span>
                </div>
              ) : (
                <Follow
                  redirectTo={redirectTo}
                  buttonText="Subscribe"
                  followedLocation={FOLLOW_LOCATIONS.FEED_VIDEO_CARD}
                  isLoggedOutState={isLoggedOutState}
                  comedianToFollow={comedian}
                  customCSSButtonClass="btn w-[unset] px-3 md:px-10 py-2.5 md:py-6 text-xs md:text-sm font-medium"
                  customCSSContainerClass="block"
                  special={special}
                  additionalEventToTrack={{
                    event: UserEvents.CLICKED_UNLOCK_SPECIAL,
                    eventProperties: {
                      comedianId: comedian.id,
                      comedianName: comedian.display_name,
                      location: FOLLOW_LOCATIONS.FEED_VIDEO_CARD,
                    },
                  }}
                  callback={followCallback}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function VideoFeedHeaderMobileOld({
  comedian,
  special,
  postAge,
  profileImg,
  userFollowsComedian,
  isLoggedOutState,
  redirectTo,
  followCallback,
  renderTimestamp,
}: {
  comedian: Comedian;
  special: SpecialRow;
  postAge: number;
  profileImg: string | null;
  userFollowsComedian: boolean;
  isLoggedOutState: boolean;
  redirectTo: string;
  followCallback: () => Promise<void>;
  renderTimestamp: (postAge: number) => string;
}) {
  const comedianPageUrl = genAbsoluteURL(`/${comedian.slug}`);

  return (
    <>
      <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden">
        <Link
          href={comedianPageUrl}
          onClick={() =>
            trackEvent(FeedEvents.CLICKED_COMEDIAN_AVATAR_VIDEO_CARD)
          }
          className="block w-full h-full"
        >
          {profileImg ? (
            <Image
              src={profileImg}
              width={40}
              height={40}
              alt={`${comedian.display_name}'s profile picture`}
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </Link>
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col">
            <Link
              href={comedianPageUrl}
              onClick={() =>
                trackEvent(FeedEvents.CLICKED_COMEDIAN_NAME_VIDEO_CARD)
              }
              className="text-white font-medium text-base hover:underline line-clamp-1"
            >
              <span>{comedian.display_name}</span>
            </Link>
            <p className="text-white/80 text-xs">{renderTimestamp(postAge)}</p>
          </div>
          <div className="ml-2 flex-shrink-0">
            {special.requires_purchase ? (
              <Link
                href={`/${comedian.slug}/${special.slug}/purchase`}
                className="bg-primary text-white px-3 py-1 rounded-md text-xs font-medium"
              >
                <WatchCTAButton special={special} />
              </Link>
            ) : userFollowsComedian ? (
              <div className="flex items-center gap-1 text-white text-xs -mt-0.5">
                <Check width={12} height={12} strokeWidth={3} />
                <span>Subscribed</span>
              </div>
            ) : (
              <Follow
                redirectTo={redirectTo}
                buttonText="Subscribe"
                followedLocation={FOLLOW_LOCATIONS.FEED_VIDEO_CARD}
                isLoggedOutState={isLoggedOutState}
                comedianToFollow={comedian}
                customCSSButtonClass="btn w-[unset] px-3 md:px-10 py-2.5 md:py-6 text-xs md:text-sm font-medium"
                customCSSContainerClass="block"
                special={special}
                additionalEventToTrack={{
                  event: UserEvents.CLICKED_UNLOCK_SPECIAL,
                  eventProperties: {
                    comedianId: comedian.id,
                    comedianName: comedian.display_name,
                    location: FOLLOW_LOCATIONS.FEED_VIDEO_CARD,
                  },
                }}
                callback={followCallback}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
